"use client"

import styled from "@emotion/styled"
import type { FocusEvent, ReactNode } from "react"
import { useId, useRef, useState } from "react"
import { useHover } from "react-aria"
import {
  Dialog,
  Popover,
  Button as ReactAriaButton,
  Tooltip,
  TooltipTrigger,
  VisuallyHidden,
} from "react-aria-components"
import { useTranslation } from "react-i18next"

import { baseTheme } from "@/shared-module/common/styles"
import { omitUndefined } from "@/shared-module/common/utils/nullability"

import { TooltipBox } from "./TooltipBox"

// oxlint-disable-next-line i18next/no-literal-string
const StyledButton = styled(ReactAriaButton)`
  text-decoration: underline;
  border: none;
  background: none;
  padding: 2px 4px;
  margin: -2px -4px;
  cursor: help;
  border-radius: 2px;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${baseTheme.colors.clear[100]};
  }

  &:focus {
    outline: 2px solid ${baseTheme.colors.blue[500]};
    outline-offset: 2px;
    background-color: ${baseTheme.colors.clear[100]};
  }

  &[data-popover-open="true"] {
    outline: 2px solid ${baseTheme.colors.blue[500]};
    outline-offset: 2px;
    background-color: ${baseTheme.colors.clear[100]};
  }
`

/**
 * Accessible glossary trigger that shows definitions on hover (tooltip) or click (popover).
 *
 * React Aria tooltips don't support opening on click, and tooltips can't be opened on mobile.
 * This component uses a tooltip on hover and a popover on click to work around these limitations.
 *
 * The aria-describedby on the tooltip trigger is overridden to show a hint instead of the full definition on focus.
 * This prevents screen readers from reading long definitions when navigating numerous,
 * repetitive glossary buttons that are common throughout the course materials.
 *
 * Think of it as an accessible popover that everyone can open with a click,
 * but sighted users can additionally preview it with hover.
 */
export const GlossaryTriggerNPopover = ({
  className,
  tooltipContent,
  children,
  dialogAriaLabel,
}: {
  className?: string
  tooltipContent: ReactNode
  children: ReactNode
  dialogAriaLabel: string
}) => {
  const { t } = useTranslation()
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const [popoverOpen, setPopoverOpen] = useState(false)

  const definitionInParenthesesId = useId()

  // When the popover closes via overlay interactions, we suppress the next focus tooltip once
  const [suppressNextFocusTooltip, setSuppressNextFocusTooltip] = useState(false)

  const buttonRef = useRef<HTMLButtonElement | null>(null)

  const handleButtonFocus = (_e: FocusEvent) => {
    if (suppressNextFocusTooltip) {
      // Skip showing a tooltip for this focus, then reset after microtask
      queueMicrotask(() => setSuppressNextFocusTooltip(false))
    }
  }

  const { hoverProps } = useHover({
    onHoverStart: () => {
      // Clear suppression when user starts hovering
      if (suppressNextFocusTooltip) {
        setSuppressNextFocusTooltip(false)
        // Allow tooltip to open immediately on hover after clearing suppression
        setTooltipOpen(true)
      }
    },
  })

  return (
    <>
      <TooltipTrigger
        isOpen={suppressNextFocusTooltip ? false : tooltipOpen}
        onOpenChange={(isOpen) => {
          // Respect suppression (e.g., right after popover closes)
          if (suppressNextFocusTooltip && isOpen) {
            return
          }
          setTooltipOpen(isOpen)
        }}
        delay={200}
        closeDelay={200}
      >
        <span {...hoverProps}>
          <StyledButton
            ref={buttonRef}
            {...omitUndefined({ className })}
            data-popover-open={popoverOpen}
            // Open the popover on press start so it appears immediately
            // when TooltipTrigger hides the tooltip on pointer/key down.
            onPressStart={() => {
              setTooltipOpen(false)
              setPopoverOpen(true)
            }}
            // Prevent the mouse press from moving focus, which can
            // re-trigger a focus-based tooltip during transitions.
            preventFocusOnPress
            onFocus={handleButtonFocus}
            aria-describedby={definitionInParenthesesId}
          >
            {children}
          </StyledButton>
          <VisuallyHidden>
            <span id={definitionInParenthesesId}>{t("press-to-view-definition")}</span>
          </VisuallyHidden>
        </span>

        <Tooltip offset={8} placement="top">
          <TooltipBox>{tooltipContent}</TooltipBox>
        </Tooltip>
      </TooltipTrigger>

      <Popover
        offset={8}
        placement="top"
        isNonModal
        isOpen={popoverOpen}
        onOpenChange={(isOpen) => {
          setPopoverOpen(isOpen)
          if (!isOpen) {
            // After closing, suppress the *next* focus tooltip so we don't flash it
            setSuppressNextFocusTooltip(true)
            // Restore focus to the trigger button, but tooltip won't open due to suppression
            queueMicrotask(() => {
              buttonRef.current?.focus()
            })
          }
        }}
        triggerRef={buttonRef}
      >
        <Dialog aria-label={dialogAriaLabel}>
          <TooltipBox>{tooltipContent}</TooltipBox>
        </Dialog>
      </Popover>
    </>
  )
}

export default GlossaryTriggerNPopover
